import { Link } from "react-router-dom";

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#070C18]">
      <div className="absolute inset-0">
        <img
          alt=""
          className="w-full h-full object-cover object-top opacity-30"
          src="https://public.readdy.ai/ai/img_res/917c41eaa36b4131e8f72347724951a8.jpg"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070C18] via-[#070C18]/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#070C18] via-transparent to-[#070C18]/60"></div>
      </div>

      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-electric-500/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-electric-400/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col gap-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-electric-500/10 border border-electric-500/20 w-fit">
              <span className="w-1.5 h-1.5 rounded-full bg-electric-400 animate-pulse"></span>
              <span className="text-electric-400 text-xs font-semibold uppercase tracking-widest">AI Workforce Automation</span>
            </div>

            <h1 className="text-5xl xl:text-6xl font-extrabold text-white leading-[1.1] tracking-tight">
              What If Your Business Could Run With{" "}
              <span className="text-electric-400">Half the Admin Staff?</span>
            </h1>

            <p className="text-xl text-slate-300 italic font-medium">No, this isn&apos;t science fiction.</p>

            <p className="text-slate-400 text-base leading-relaxed max-w-xl">
              The Project Experts deploys modular AI agents that handle your intake, scheduling, documents, and internal knowledge — automatically, from day one. Start with one module. Expand as you grow.
            </p>

            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-1.5">
                <i className="ri-shield-check-line text-electric-400"></i>
                <span>No IT project needed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-code-s-slash-line text-electric-400"></i>
                <span>Zero coding required</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-flashlight-line text-electric-400"></i>
                <span>Results in days</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-electric-500 text-white font-semibold text-base hover:bg-electric-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                to="/how-it-works"
              >
                See How It Works
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-white/5 text-white font-semibold text-base border border-white/15 hover:bg-white/10 hover:border-electric-500/40 transition-all duration-200 whitespace-nowrap cursor-pointer"
                to="/contact"
              >
                Book a Free Assessment
                <i className="ri-calendar-line"></i>
              </Link>
            </div>
          </div>

          <div className="hidden lg:flex justify-center">
            <div className="relative w-full max-w-lg">
              <div className="absolute inset-0 bg-electric-500/10 rounded-3xl blur-2xl scale-110"></div>
              <div className="relative bg-[#0D1628]/90 border border-white/10 rounded-3xl p-6 backdrop-blur-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider">AI Dashboard</p>
                    <p className="text-white font-semibold text-lg">Live Agent Activity</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                    <span className="text-emerald-400 text-xs font-medium">4 Active</span>
                  </div>
                </div>

                {[
                  { icon: "ri-inbox-2-line", color: "blue", label: "AI Intake Agent", desc: "Qualified 3 new leads", time: "2 min ago" },
                  { icon: "ri-calendar-check-line", color: "cyan", label: "AI Appointment Maker", desc: "Booked consultation — Müller GmbH", time: "8 min ago" },
                  { icon: "ri-file-text-line", color: "violet", label: "AI Document Intelligence", desc: "Summarised 14-page contract", time: "15 min ago" },
                  { icon: "ri-robot-line", color: "emerald", label: "AI Knowledge Assistant", desc: "Answered 7 staff queries", time: "1 hr ago" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 p-3 rounded-xl hover:bg-white/5 transition-colors mb-2 group cursor-default"
                  >
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl text-${item.color}-400 bg-${item.color}-500/10 shrink-0`}>
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium">{item.label}</p>
                      <p className="text-slate-400 text-xs truncate">{item.desc}</p>
                    </div>
                    <span className="text-slate-500 text-xs whitespace-nowrap shrink-0">{item.time}</span>
                  </div>
                ))}

                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-white/5">
                  <div className="text-center p-2.5 rounded-xl bg-white/5">
                    <p className="text-electric-400 font-bold text-lg">€12,400</p>
                    <p className="text-slate-500 text-xs mt-0.5">Admin Cost Saved</p>
                  </div>
                  <div className="text-center p-2.5 rounded-xl bg-white/5">
                    <p className="text-electric-400 font-bold text-lg">284</p>
                    <p className="text-slate-500 text-xs mt-0.5">Tasks Automated</p>
                  </div>
                  <div className="text-center p-2.5 rounded-xl bg-white/5">
                    <p className="text-electric-400 font-bold text-lg">99.2%</p>
                    <p className="text-slate-500 text-xs mt-0.5">Uptime</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#070C18] to-transparent"></div>
    </section>
  );
}
